import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, UserCog } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function CoordinatorUsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function invite(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      await api.post("/users", { email: email.trim(), role: "coordinator" });
      setEmail("");
      toast.success("Coordenador convidado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  }

  async function confirmDelete() {
    try {
      await api.delete(`/users/${deleteId}`);
      setDeleteId(null);
      await load();
    } catch (e) { toast.error("Erro"); }
  }

  return (
    <div className="max-w-3xl">
      <PageHeader title="Coordenadores do setor" subtitle="Convide outros coordenadores para compartilhar a gestão deste setor." />

      <Card className="p-6 mb-6">
        <form onSubmit={invite} className="flex gap-2">
          <Input
            data-testid="coordinator-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@ifpr.edu.br"
          />
          <Button data-testid="invite-coordinator-btn" type="submit" disabled={saving} className="bg-[#1e5a8e] hover:bg-[#164570]">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Plus className="w-4 h-4 mr-1" /> Convidar
          </Button>
        </form>
        <p className="text-xs text-slate-500 mt-2">
          O e-mail deve pertencer a um domínio autorizado. O usuário entra via Google.
        </p>
      </Card>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : users.length === 0 ? (
        <EmptyState icon={UserCog} title="Nenhum coordenador ainda" description="Convide colegas para gerenciar este setor com você." />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">E-mail</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Papel</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-6 py-4 text-sm text-slate-900">{u.name || "—"}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{u.email}</td>
                  <td className="px-6 py-4"><Badge variant="outline">Coordenador</Badge></td>
                  <td className="px-6 py-4 text-right">
                    {u.email !== user.email && (
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setDeleteId(u.id)} data-testid={`delete-coord-${u.email}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover coordenador?</AlertDialogTitle>
            <AlertDialogDescription>O usuário perderá acesso a este setor.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
